import axios from 'axios';
import { toast } from 'react-toastify';
import config from '../config/apiConfig';



/**
 * Obtiene la lista de productos para una sucursal específica.
 * @param {function} setLoading - Función de estado para controlar el estado de carga.
 * @param {function} setProductos - Función de estado para almacenar la lista de productos.
 * @param {function} setProductosFiltrados - Función de estado para almacenar los productos filtrados.
 * @param {function} setError - Función de estado para manejar los mensajes de error.
 */
export async function getProductos(setLoading) {
    // Se obtiene la sucursal del almacenamiento local. Si no existe, se usa un objeto vacío.
    const sucursal = JSON.parse(localStorage.getItem("sucursal")) || {};
    
    // Se verifica si el ID de la sucursal existe antes de hacer la llamada a la API.
    if (!sucursal.id) {
        toast.error("No se ha encontrado una sucursal válida para consultar productos.");
        setLoading(false);
        return;
    }

    setLoading(true);

    try {
        const resp = await axios.get(`${config.api_url}/productos/?sucursalId=${sucursal.id}`);
        if (resp.data.completado) {
            const productos = resp.data.productos;
            return productos;
            //setProductos(productos);
            //setProductosFiltrados(productos);
        } else {
            //setError(resp.data.mensaje);
            toast.error(resp.data.mensaje);
            return [];
        }
    } catch (err) {
        console.error("Error al obtener los productos:", err);
        toast.error("Se ha producido un error al intentar obtener los productos.");
        return [];
       
    } finally {
        setLoading(false);
    }
}

/**
 * Obtiene la lista completa de precios desde la API.
 * @param {function} setLoading - Función de estado para controlar el estado de carga.
 * @param {function} setListas - Función de estado para almacenar la lista de precios.
 * @param {function} setError - Función de estado para manejar los mensajes de error.
 */
export async function getListas(setLoading) {
    setLoading(true);
    try {
        const resp = await axios.get(`${config.api_url}/listas/`);
        
        if (resp.data.completado) {
            return resp.data.listas;
        } else {
            // Maneja el caso de que la respuesta de la API no sea 'completada'
            toast.error(resp.data.mensaje || "Ocurrió un error al obtener las listas.");
            return [];
        }
    } catch (err) {
        // Captura errores de red o del servidor
        console.error("Error al obtener las listas de precios:", err);
        //setError("Se ha producido un error al intentar obtener las listas.");
        toast.error("Se ha producido un error al intentar obtener las listas.");
        return [];
    } finally {
        // Asegura que el estado de carga se desactive en cualquier caso
        setLoading(false);
    }
}

/**
 * Consulta la API para obtener una lista de productos que coincidan con el campo de búsqueda,
 * aplicando el precio de la lista de precios seleccionada.
 * @param {string} campoBusqueda - El criterio de búsqueda (ej: código parcial, nombre).
 * @param {object} listaPrecioSeleccionada - El objeto de la lista de precios seleccionada.
 * @returns {Promise<Array<object>>} Una promesa que resuelve con un array de productos mapeados.
 */
export const obtenerProductosApi = async (campoBusqueda, listaPrecioSeleccionada) => {
    
    const listaPrecioId = listaPrecioSeleccionada ? listaPrecioSeleccionada.id : null;
    
    // 💡 IMPORTANTE: Si la URL debe cambiar para una búsqueda múltiple, ajústala aquí.
    // Asumiré que este endpoint ya puede devolver varios resultados.
    const url = `${config.api_url}/producto/consulta/n/`; 
    
    console.log(`[AXIOS] Buscando productos con criterio: "${campoBusqueda}" lista precio "${listaPrecioId}" `);

    try {
        const response = await axios.get(url, {
            params: {
                campoBusqueda: campoBusqueda,
                listaPrecioId: listaPrecioId 
            }
        });

        console.log("respuesta servidor", response);

        // --- 💡 Lógica Modificada para Múltiples Productos ---
        
        // 1. Asumir que el backend retorna: {'completado': True, 'productos': [{...}, {...}, ...]}
        if (response.data && response.data.completado && Array.isArray(response.data.productos)) {
            
            const productos = response.data.productos;
            
            // 2. Mapear la lista de productos
            return productos.map(producto => {
                // Mapeo: Transforma tu respuesta (ej: {nombre} y {valor}) al formato del frontend
                return {
                    id: producto.id,
                    descripcion: producto.descripcion,
                    precio: producto.precio, // Campo 'valor' ajustado por la lista de precios
                    sku: producto.sku,
                    // Agrega cualquier otro campo que el frontend necesite
                };
            });
        }
        
        // Retorna un array vacío si no se encuentran productos o si el formato no es el esperado
        // (por ejemplo, si 'productos' no existe o no es un array, o 'completado' es falso).
        return [];

    } catch (error) {
        console.error("Error en obtenerProductos:", error);
        // Es mejor re-lanzar un error si la conexión falló
        throw new Error("Fallo la conexión con el servicio de productos."); 
    }
};


/**
 * Función para enviar una solicitud POST al backend de VentaGix para crear un nuevo producto.
 * Utiliza el endpoint real de tu API: /api/productos
 * @param {object} datosProducto - Objeto con los datos del nuevo producto a crear.
 * @param {string} token - El Token JWT obtenido durante el inicio de sesión.
 * @returns {Promise<object>} Devuelve el objeto de la respuesta, incluyendo el ID del nuevo producto.
 */
export const crearItemMenu = async (datosProducto, token) => {
    
    // Ruta específica: /api/productos
    const rutaEndpoint = '/api/productos'; 
    const urlCompleta = `${config.api_url}${rutaEndpoint}`;
    
    try {
        const respuesta = await axios.post(
            urlCompleta, 
            datosProducto, // Axios serializa automáticamente el cuerpo JSON
            {
                headers: {
                    // 🔑 CLAVE DE SEGURIDAD CORRECTA para VentaGix
                    "token": token, 
                    // No necesitamos Content-Type, Axios lo maneja automáticamente
                }
            }
        );

        // Axios devuelve la respuesta en 'data', y lanza un error para 4xx/5xx
        // Por lo que el manejo de errores es más limpio aquí.

        // Si llega aquí, la respuesta es 2xx (ej: 201 Created)
        return respuesta.data;

    } catch (error) {
        
        // Manejo específico de errores de Axios
        if (error.response) {
            // El servidor respondió con un código de estado (4xx o 5xx)
            const status = error.response.status;
            const mensaje = error.response.data.message || `Error del servidor (Código ${status}).`;
            
            console.error(`Fallo API [${status}]:`, mensaje);
            // Propagamos el error de forma legible
            throw new Error(mensaje); 
        } else if (error.request) {
            // La solicitud fue hecha pero no se recibió respuesta (ej: CORS, servidor caído)
            throw new Error("No se pudo conectar con el servidor API. Verifique que Flask esté activo.");
        } else {
            // Algo más causó el error
            throw new Error(`Error desconocido de Axios: ${error.message}`);
        }
    }
};


export const actualizarItemMenu = async (idProducto, datosProducto) => {
    
    const token = localStorage.getItem("token");
    if (!token) throw new Error("Sin token");
    const url = `${config.api_url}/api/productos/${idProducto}`;

    // 🔑 DEBUG CRÍTICO
    console.log("datosProducto:", datosProducto);

    /*const formData = new FormData();

    formData.append("nombre", datosProducto.nombre || "");
    formData.append("descripcion", datosProducto.descripcion || "");
    formData.append("precio", datosProducto.precio || "");
    formData.append("stock", datosProducto.stock || "");
    formData.append("categoria_id", datosProducto.categoria_id || "");
    formData.append("activo", datosProducto.activo ? 1 : 0);
    formData.append("tiene_variantes", datosProducto.tiene_variantes ? 1 : 0);

    // 🔴 SI HAY IMAGEN
    if (datosProducto.imagen instanceof File) {
        formData.append("imagen", datosProducto.imagen);
    }

    */
    // 🔍 DEBUG DEFINITIVO
    for (let pair of datosProducto.entries()) {
        console.log(pair[0] + ':---', pair[1]);
    }

    const respuesta = await axios.put(url, datosProducto, {
        headers: {
            token: token
            // 🚨 NO Content-Type
        },
        //transformRequest: data => data // 🔑 evita serialización
    });

    return respuesta.data;
};



/**
 * Obtiene la lista de todos los productos del negocio al que pertenece el token.
 * Llama al endpoint GET /api/productos.
 * * @returns {Promise<Array<object>>} Promesa que resuelve en una lista de productos.
 * @throws {Error} Si la solicitud falla o no se encuentra el token.
 */
export async function obtenerTodosLosProductos() {
    // 1. Obtener el Token (Necesario para tu endpoint con @token_requerido)
    const token = localStorage.getItem("token");
    if (!token) {
        // En lugar de lanzar toast aquí, es mejor lanzar un error para que el componente lo maneje
        throw new Error("No se encontró token de autenticación para listar productos.");
    }
    
    const url = `${config.api_url}/api/productos`; 
    try {
        const resp = await axios.get(url, {
            headers: {
                // 🔑 CLAVE DE SEGURIDAD CORRECTA para VentaGix
                "token": token, 
            }
        });

        // Tu backend de Flask devuelve la lista de productos o un error 500.
        // Si la respuesta es 200 (OK), `resp.data` es la lista de productos.
        if (resp.status === 200) {
            // El backend retorna un array directo, lo devolvemos
            console.log("productos", resp.data);
            return resp.data; 
        }
        
        // Esto generalmente no debería ocurrir con Axios, pero es una protección
        toast.error("Respuesta inesperada del servidor al listar productos.");
        return [];

    } catch (error) {
        
        // Manejo de error de conexión o 4xx/5xx
        let mensajeError = 'Error de conexión con el servicio de productos.';
        if (error.response && error.response.data && error.response.data.error) {
            mensajeError = error.response.data.error; // Usa el mensaje de error del backend
        } else if (error.message.includes('401')) {
            mensajeError = 'Acceso denegado. Token inválido o expirado.';
        }
        
        console.error("Error al obtener productos:", error);
        toast.error(mensajeError);
        
        // Lanzamos el error para que el componente lo capture y cambie el estado
        throw new Error(mensajeError); 
    }
}


/**
 * Función para obtener el listado de productos disponibles para la venta
 * y su respectivo flag de variantes (tieneVariantes).
 * * @returns {Promise<{productos: Array, error: string|null}>}
 */

export async function getProductosCatalogo() {
    try {
        // 1. Obtener Token y Negocio ID
        const token = localStorage.getItem("token");
        
        if (!token) {
            return { productos: [], error: "Token de sesión no encontrado." };
        }

        // 2. Configurar Axios con el token
        axios.defaults.headers.common["token"] = token;

        // 3. Llamar al nuevo endpoint optimizado
        const url = `${config.api_url}/api/productos/catalogo`;

        const response = await axios.get(url);
        console.log("respuesta", response);

        // 4. Procesar la Respuesta del Backend
        if (response.status === 200) {
            return { 
                productos: response.data, 
                error: null 
            };
        } else {
            // Este bloque es redundante si Axios lanza error para 4xx/5xx,
            // pero lo dejamos por seguridad si el servidor responde 200 con un error interno.
            return { productos: [], error: "Error de servidor al obtener el catálogo." };
        }

    } catch (error) {
        // 🚨 CORRECCIÓN CLAVE: Reemplazar '?' por la comprobación '&&'
        const errorMessage = (error.response && error.response.data && error.response.data.error) 
                             || "Error de conexión con la API.";
        
        console.error("Error al obtener catálogo:", error);
        return { productos: [], error: errorMessage };
    }
}

export async function getProductosCatalogoTienda(slug) {
    try {
        

        // 3. Llamar al nuevo endpoint optimizado
        const url = `${config.api_url}/api/productos/catalogo/publico/`;

        const response = await axios.get(url + slug);
        console.log("respuesta", response);

        // 4. Procesar la Respuesta del Backend
        if (response.status === 200) {
            return { 
                productos: response.data, 
                error: null 
            };
        } else {
            // Este bloque es redundante si Axios lanza error para 4xx/5xx,
            // pero lo dejamos por seguridad si el servidor responde 200 con un error interno.
            return { productos: [], error: "Error de servidor al obtener el catálogo." };
        }

    } catch (error) {
        // 🚨 CORRECCIÓN CLAVE: Reemplazar '?' por la comprobación '&&'
        const errorMessage = (error.response && error.response.data && error.response.data.error) 
                             || "Error de conexión con la API.";
        
        console.error("Error al obtener catálogo:", error);
        return { productos: [], error: errorMessage };
    }
}

/**
 * Elimina un producto de forma lógica en la BD
 * @param {number} id - ID del producto a eliminar
 */
export const eliminarProducto = async (id) => {

    
    const token = localStorage.getItem("token");
    
    const response = await fetch(config.api_url + `/api/productos/${id}`, {
        method: 'DELETE',
        headers: {
            'Content-Type': 'application/json',
            'token': token // Usamos tu estándar de cabecera 'token'
        }
    });

    if (!response.ok) {
        const error = await response.json();
        throw new Error(error.mensaje || 'No se pudo eliminar el producto');
    }

    return await response.json();
};


{/*Productos mas vendidos */}
export const obtenerReporteProductos = async (fechaInicio, fechaFin) => {
    const token = localStorage.getItem("token");
    const url = `${config.api_url}/api/reportes/productos-mas-vendidos?inicio=${fechaInicio}&fin=${fechaFin}`;
    
    const res = await fetch(url, {
        headers: { 'token': token }
    });
    return await res.json();
};


{/*Reporte listado de pedidos */}
export const obtenerListadoPedidos = async (inicio, fin) => {
    const token = localStorage.getItem("token");
    const res = await fetch(`${config.api_url}/api/reportes/listado-pedidos?inicio=${inicio}&fin=${fin}`, {
        headers: { 'token': token }
    });
    return await res.json();
};


// Añadir a productosApi.js (o como se llame tu archivo Axios/Fetch)
export const ajusteRapidoStock = async (productoId, nuevoStock) => {
    // Necesitas tener este endpoint en tu Flask para que solo actualice el campo stock
    const token = localStorage.getItem('token');
    const response = await fetch(`${config.api_url}/api/productos/${productoId}/stock_rapido`, {
        method: 'PUT',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ stock: nuevoStock })
    });
    
    if (!response.ok) throw new Error("Error ajustando stock");
    return await response.json();
};



{/*nuevos */}
// En tu archivo de servicios (ej: src/services/productosApi.js)
// En src/services/productosApi.js

// Función para crear producto V2
export const crearItemMenuV2 = async (formData) => {
    const token = localStorage.getItem('token');
    const res = await fetch(`${config.api_url}/api/v2/productos/crear`, {
        method: 'POST',
        headers: { 
            'token': token // 🚩 Header solicitado
        },
        body: formData
    });

    if (res.ok) {
        try {
            return await res.json();
        } catch (e) {
            // Si el servidor responde 200/201 pero no es JSON, devolvemos éxito manual
            return { status: 'success', message: 'Creado' };
        }
    }
    throw new Error("Error servidor: " + res.status);
};

// Función para actualizar producto V2
export const actualizarItemMenuV2 = async (id, formData) => {
    const token = localStorage.getItem('token');
    const res = await fetch(`${config.api_url}/api/v2/productos/actualizar/${id}`, {
        method: 'PUT',
        headers: { 
            'token': token // 🚩 Header solicitado
        },
        body: formData
    });

    if (res.ok) {
        try {
            return await res.json();
        } catch (e) {
            return { status: 'success', message: 'Actualizado' };
        }
    }
    throw new Error("Error servidor: " + res.status);
};

// Obtener plantillas de la biblioteca
export const obtenerPlantillasVariantes = async () => {
    const token = localStorage.getItem('token');
    const res = await fetch(`${config.api_url}/api/v2/variantes/plantillas`, {
        headers: { 'token': token } // 🚩 Header solicitado
    });
    if (res.ok) return await res.json();
    return [];
};

// Guardar en biblioteca
export const guardarPlantillaVariante = async (datos) => {
    const token = localStorage.getItem('token');
    const res = await fetch(`${config.api_url}/api/v2/variantes/plantillas`, {
        method: 'POST',
        headers: { 
            'token': token, // 🚩 Header solicitado
            'Content-Type': 'application/json' 
        },
        body: JSON.stringify(datos)
    });
    if (res.ok) return await res.json();
    return { error: 'fallo' };
};

// SETEA ORDEN VISUAL EN administrador de productos
export const actualizarOrdenVisual = async (id, nuevoOrden) => {
    const token = localStorage.getItem('token');
    const formData = new FormData();
    formData.append('orden_visual', nuevoOrden);

    const url = `${config.api_url}/api/productos/${id}/orden`;
    console.log("Llamando a:", url); // Para que veas la URL en la consola

    const response = await fetch(url, {
        method: 'POST', 
        headers: { 'token': token },
        body: formData
    });

    // SI LA RESPUESTA NO ES OK, NO INTENTES HACER .JSON()
    if (!response.ok) {
        const textoError = await response.text(); // Leemos como texto, no JSON
        console.error("El servidor respondió con HTML (posible 404 o 500):", textoError);
        throw new Error(`Error ${response.status}: Revisa la consola para ver el HTML`);
    }

    return response.json();
};